import {
    ChangeDetectionStrategy,
    Component,
    inject,
    Injector,
    Input,
    OnChanges,
    SimpleChanges,
    ViewEncapsulation,
} from '@angular/core';
import { FiInfiniteScrollDataSource } from '@fi-shared/fi-items-list/fi-infinite-scroll-data-source';
import { IOnboardingReportParticipant } from '@fi-app/fi-onboarding/fi-onboarding-report/fi-onboarding-report';
import { FiItemsListType } from '@fi-shared/fi-items-list/fi-items-list-type';
import { ObjectTypes } from '@fi-shared/types';
import { FiStateService } from '@fi-shared/fi-state-proxy/fi-state.proxy.service';
import { FiStateNames } from '@fi-shared/fi-state-proxy/fi-state-names';
import { FiTooltipContentType } from '@fi-shared/fi-tooltip/fi-tooltip-enums';
import { getObjectTypeNameTextId } from '@fi-shared/utils';
import { FiSharedActionsService } from '@fi-app/utilities/fi-services/fi-shared-actions.service';
import { AppTextService } from '@fi-shared/app-text';
import { INameIdObj } from '@fi-shared/fi-shared-interfaces';

@Component({
    selector: 'fi-onboarding-report-participants-onboarded-widget-table',
    templateUrl: './fi-onboarding-report-participants-onboarded-widget-table.component.html',
    styleUrl: './fi-onboarding-report-participants-onboarded-widget-table.component.scss',
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: { class: 'fi-onboarding-report-participants-onboarded-widget-table' },
    standalone: false,
})
export class FiOnboardingReportParticipantsOnboardedWidgetTableComponent
    extends FiInfiniteScrollDataSource<IOnboardingReportParticipant>
    implements OnChanges
{
    @Input({ required: true }) onboardingParticipants: IOnboardingReportParticipant[];
    @Input({ required: true }) widgetId: number;

    private readonly sharedActionsService = inject(FiSharedActionsService);
    private readonly appTextService = inject(AppTextService);

    public readonly itemsListType = FiItemsListType.OnboardingReportParticipants;
    public readonly objectType = ObjectTypes.OnboardingReport;
    public readonly defaultSortColumnName = 'CompletedDate';
    public readonly defaultSortDirection = true;

    private readonly fiStateService = inject(FiStateService);

    protected getRowClassFn = (item: IOnboardingReportParticipant): string => 'cursor-pointer';

    constructor(injector: Injector) {
        super(injector);
    }

    public ngOnChanges(changes: SimpleChanges): void {
        const change = changes.onboardingParticipants;
        if (change) {
            this.reloadData();
        }
    }

    public getListData(): Promise<IOnboardingReportParticipant[]> {
        return Promise.resolve(this.onboardingParticipants);
    }

    protected getBusinessUnits(item: IOnboardingReportParticipant): INameIdObj[] {
        return item.BusinessUnits;
    }

    protected isAssignedUnitsTooltipVisible(item: IOnboardingReportParticipant): boolean {
        return item.BusinessUnits.length > 1;
    }

    protected getAssignedUnitsText(item: IOnboardingReportParticipant): string {
        const result: string[] = [];
        const businessUnits = item.BusinessUnits;
        if (businessUnits.length === 1) {
            result.push(businessUnits[0].Name);
        } else if (businessUnits.length > 0) {
            const businessUnitsLabel = this.appTextService
                .get(getObjectTypeNameTextId(ObjectTypes.BusinessUnit, businessUnits.length > 1))
                .toLocaleLowerCase();
            result.push(`${businessUnits.length} ${businessUnitsLabel}`);
        }
        if (!result.length) {
            return '-';
        }
        return result.join(', ');
    }

    protected openUnitDetails($event: MouseEvent, unitId: number): void {
        $event.stopPropagation();
        this.sharedActionsService.openUnitViewPopup(unitId);
    }

    protected onRowClick(item: IOnboardingReportParticipant): void {
        this.fiStateService.go(FiStateNames.Onboarding.Report.Widget.ParticipantDetails._name, {
            id: this.widgetId,
            userId: item.Id,
            onboardingId: item.OnboardingId,
            //businessUnitId: item.BusinessUnitId,
        });
    }

    protected readonly FiTooltipContentType = FiTooltipContentType;
}
